import { useCallback, useContext } from 'react'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { HeaderNav, TypeEditableTitle, Status } from '@components/layout/headerNav'
import { TitleWithBadge } from '@components/titleWithBadge'
import { submissionContext } from '@contexts'
import { colors } from '@global/constants'
import { useFetchApprovalRequests } from '@global/hooks'
import { useApprovalsEnabled } from '@global/hooks/useApprovalsEnabled'
import { useTemplate } from '@global/hooks/useTemplate'
import { useAppSelector } from '@redux/hooks'
import { t } from '@lingui/core/macro'

interface Props {
  templateId: string
  title?: string | null
  status?: Status | null
  editableTitleCallback?: TypeEditableTitle | null
}

/**
 * The header and navigation component for Submissions;
 * including the selective edit header on path /codes/details/:id/selective.
 * @param props React Props
 * @param {string} props.templateId The template ID used to get the template from store.
 * @param {string} props.title The submission name.
 * @param {Status} props.status The UI pill status for the flow stage of the submission.
 * @param {TypeEditableTitle} props.editableTitleCallback The onChange event function that handles submission name changes.
 * @returns {ReactNode}
 */
export const SubmissionsHeader = ({
  title = 'null',
  templateId,
  status = null,
  editableTitleCallback = null,
}: Props) => {
  const { template } = useTemplate({ templateId })
  const { hasUnseenDrafts } = useContext(submissionContext)

  const [DATASETS_PAGE_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )
  const isApprovalsEnabled = useApprovalsEnabled()

  const isSelected = useCallback(
    function isSelected(name) {
      const currPathParts = location.pathname.split('/').filter(c => c)
      const currentPageName = currPathParts[currPathParts.length - 1]

      return currentPageName === name
    },
    [location.pathname],
  )

  const { approvalRequestsLoading, approvalRequestsCount } = useFetchApprovalRequests({
    showApprovalRequests: isApprovalsEnabled,
  })

  const headerLinks = [
    ...(DATASETS_PAGE_ENABLED
      ? []
      : [{ label: 'Templates', path: '/codes/templates', selected: isSelected('templates') }]),
    { label: 'Submissions', path: '/codes/submissions', selected: isSelected('submissions') },
    {
      label: 'Pending',
      path: '/codes/pending',
      selected: isSelected('pending'),
      badge: () => (
        <TitleWithBadge
          data-testid="pending-title"
          isLoading={false}
          dotOnly={true}
          color={hasUnseenDrafts ? colors.green.main : 'transparent'}
          title={t`Pending`}
        />
      ),
    },
    { label: 'Search', path: '/codes/search', selected: isSelected('search') },
    {
      label: 'Approval Requests',
      path: '/codes/approval',
      visible: isApprovalsEnabled,
      selected: isSelected('approval'),
      badge: () => (
        <TitleWithBadge
          isLoading={approvalRequestsLoading}
          count={approvalRequestsCount}
          title={t`Approval requests`}
        />
      ),
    },
  ]

  const isApprovalReviewPath =
    Boolean(location.pathname.includes('approval')) && Boolean(location.pathname.includes('review'))
  const hideHeaderLinks = isApprovalReviewPath

  let featureFlagTitle = title
  if (featureFlagTitle === 'Templates') {
    featureFlagTitle = DATASETS_PAGE_ENABLED ? 'Datasets' : 'Templates'
  }

  return (
    <HeaderNav
      title={featureFlagTitle || template?.name}
      status={status}
      links={hideHeaderLinks ? [] : headerLinks}
      editableTitleCallback={editableTitleCallback}
    />
  )
}
